/**
 * agent-stream — live SSE implementation of {@link AgentStreamPort}
 * (F-AGENT PR-A).
 *
 * Wraps `POST /api/v1/agent/stream` in a typed async iterable. The BE
 * emits one JSON payload per `data:` line with the shape
 * `{ type, data, ... }`; the SSE `event:` line is ignored because the
 * discriminator lives inside the payload. First frame is always
 * `{"type":"status","data":"connecting"}`.
 *
 * Hand-rolled validation (no zod), per the `auth-http` / `search-http` /
 * `citation-resolver-http` precedent. The validator mirrors the FE
 * `parseAgentSSEEvent` mapping (`src/lib/sse-schemas.ts`) but NARROWED to
 * the add-in's `AgentEvent` union — FE-only trace members are dropped to
 * `null` (skipped), unknown types are skipped.
 *
 * W-invariants honoured:
 *   - W1: this layer only transports + validates the wire. No legal
 *     decision is computed — the BE owns every reasoning step.
 *   - W6: the validator has NO `usage` case and DEFENSIVELY drops any
 *     frame carrying a cost/token/usage key, even on an otherwise-known
 *     event type. No `$`/token-shaped data ever reaches the domain/UI.
 *   - W7: `citations` / `registry` chips are passed through verbatim from
 *     the BE payload; the add-in never invents a chip.
 */

import type { AgentStreamPort, AgentStreamInput } from '@/domain/agent/ports'
import type {
  AgentEvent,
  AgentCitation,
  AgentCitationChip,
  AgentChipLanguage,
} from '@/domain/agent/entity'
import { createSSEStream } from './parser'

export interface AgentStreamConfig {
  /** Backend base URL (no trailing slash). */
  baseUrl: string
  /** Lazy bearer accessor — read at call time (login/logout mid-session). */
  getAccessToken: () => string | null
  /** Fetch override for tests. Defaults to the global. */
  fetch?: typeof fetch
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function readString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function readNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function readStringOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

/** W6 — the cost/token/usage key vocabulary the user-facing wire must
 *  NEVER carry. The BE already scrubs these (`emit_usage` retired,
 *  api#551), but the add-in re-asserts the firewall defensively: any
 *  frame whose payload or its direct `data` object contains one of these
 *  keys is dropped wholesale. Substring match so `total_tokens`,
 *  `prompt_tokens`, `estimated_cost_usd`, … are all caught.
 *
 *  Depth-capped at 1 (payload keys + `data`'s direct keys) on purpose: a
 *  cost field on a usage/done frame lives at `data.total_tokens` (depth 1).
 *  We must NOT recurse into the `registry` frame's opaque
 *  `data.articles`/`data.decisions` records — a future per-entry key merely
 *  *containing* 'cost'/'token' (e.g. `last_cost_review`) would otherwise drop
 *  the ENTIRE registry frame and silently kill every chip (W7 blast radius).
 *  Cost data is never nested that deep on the agent wire. */
const COST_KEY_FRAGMENTS = ['usage', 'token', 'cost'] as const
const COST_SCAN_MAX_DEPTH = 1

function hasCostKey(obj: Record<string, unknown>, depth = 0): boolean {
  for (const key of Object.keys(obj)) {
    const lower = key.toLowerCase()
    if (COST_KEY_FRAGMENTS.some((frag) => lower.includes(frag))) return true
    if (depth < COST_SCAN_MAX_DEPTH) {
      const value = obj[key]
      if (isPlainObject(value) && hasCostKey(value, depth + 1)) return true
    }
  }
  return false
}

function readChipLanguage(v: unknown): AgentChipLanguage {
  return v === 'ar' || v === 'en' ? v : 'fr'
}

/** Map one BE `citations[]` entry → narrowed `AgentCitation`. */
function mapCitation(raw: unknown): AgentCitation {
  const w = isPlainObject(raw) ? raw : {}
  return {
    refKey: readStringOrNull(w.ref_key),
    label: readString(w.label),
    countryCode: readString(w.country_code) || readString(w.country),
    score: readNumber(w.score),
    verified: w.verified === true,
  }
}

/** Map one BE `registry.chips[refKey]` descriptor → `AgentCitationChip`.
 *  The BE wire is camelCase here (mirrors `ChipDescriptor`). Returns
 *  `null` for a chip missing its canonical `refKey` (W7 — never invent). */
function mapChip(refKey: string, raw: unknown): AgentCitationChip | null {
  if (!refKey) return null
  const w = isPlainObject(raw) ? raw : {}
  return {
    refKey,
    articleNumber: readStringOrNull(w.articleNumber),
    codeName: readStringOrNull(w.codeName),
    countryCode: readString(w.countryCode),
    language: readChipLanguage(w.language),
  }
}

/**
 * Validate + narrow one parsed SSE payload into an {@link AgentEvent}.
 * Returns `null` to skip: FE-only trace members, unknown types, and any
 * cost-keyed payload (W6).
 */
export function validateAgentEvent(raw: unknown): AgentEvent | null {
  if (!isPlainObject(raw)) return null
  // W6 firewall — drop any frame carrying a cost/token/usage key, even on
  // an otherwise-known type, before we look at `type`.
  if (hasCostKey(raw)) return null

  const type = raw.type
  switch (type) {
    case 'status':
      return { type: 'status', data: readString(raw.data) }
    case 'chunk':
      return { type: 'chunk', data: readString(raw.data) }
    case 'citations': {
      const list = Array.isArray(raw.data) ? raw.data.map(mapCitation) : []
      return { type: 'citations', data: list }
    }
    case 'registry': {
      const data = isPlainObject(raw.data) ? raw.data : {}
      const chipsRaw = isPlainObject(data.chips) ? data.chips : {}
      const chips: AgentCitationChip[] = []
      for (const refKey of Object.keys(chipsRaw)) {
        const chip = mapChip(refKey, chipsRaw[refKey])
        if (chip) chips.push(chip)
      }
      return { type: 'registry', chips }
    }
    case 'confidence':
      return { type: 'confidence', data: readNumber(raw.data) }
    case 'done':
      return { type: 'done' }
    case 'error':
      return { type: 'error', data: readString(raw.data) }
    case 'abstention': {
      const data = isPlainObject(raw.data) ? raw.data : {}
      const suggestions = Array.isArray(data.suggestions)
        ? data.suggestions.filter((s): s is string => typeof s === 'string')
        : []
      const reason = readString(data.reason)
      return {
        type: 'abstention',
        message: readString(data.message),
        suggestions,
        ...(reason ? { reason } : {}),
      }
    }
    // FE-only trace members (subagent_verdict, step_content, plan,
    // methodology, chunk_retag, agent_decision, citation_check,
    // conversation, …) and any unknown type → skip.
    default:
      return null
  }
}

export function createAgentStreamHttp(config: AgentStreamConfig): AgentStreamPort {
  const doFetch =
    config.fetch ?? ((input, init) => globalThis.fetch(input, init))

  return {
    async *stream(
      input: AgentStreamInput,
      opts: { signal: AbortSignal },
    ): AsyncIterable<AgentEvent> {
      const token = config.getAccessToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      }
      if (token) headers['Authorization'] = `Bearer ${token}`

      // snake_case body — the BE `AgentChatRequest` reads `case_id`,
      // `history`, `conversation_id` snake-cased. `language` is RESERVED:
      // `AgentChatRequest` has no `language` field yet, so Pydantic
      // (`extra='ignore'`) silently drops it — harmless, no 422. Sent now so
      // the wire is ready the day the BE honors it; until then the agent
      // infers locale from the message itself.
      const body: Record<string, unknown> = { message: input.message }
      if (input.caseId) body.case_id = input.caseId
      if (input.conversationId) body.conversation_id = input.conversationId
      if (input.history && input.history.length > 0) body.history = input.history
      if (input.language) body.language = input.language

      const res = await doFetch(`${config.baseUrl}/api/v1/agent/stream`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
        signal: opts.signal,
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`Agent stream failed ${res.status}: ${text}`)
      }

      for await (const ev of createSSEStream(res, validateAgentEvent, {
        signal: opts.signal,
      })) {
        yield ev.data
      }
    },
  }
}
